/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  createProject,
  deleteProject,
  deleteManyProjects,
} from '@src/apis/project';
import actions from '@src/redux/actions';
import { useTranslation } from 'react-i18next';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/Delete';
import sanitize from 'sanitize-html';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import route from '@src/constants/route';
import { EXTRACTION_STATUS } from '@src/constants/project';
import { trackProjectCreated } from '@src/services/moengage';
import { MOE_PROJECT_SOURCE_TYPE } from '@src/constants/moengage';
import Table from '@src/components/NewTable';
import { StyledProjectList } from './index.style';
import Header from './Header';
import Actions from './Actions';
import BannerCreateSampleProject from './BannerCreateSampleProject';
import TitleProject from './TitleProject';
import NoData from './NoData';

const ProjectList = ({
  projects,
  hasAnyProject,
  loading,
  handleSearchInputChange,
  search,
  onDeleteSuccess,
  onDeleteManySuccess,
  total,
  page,
  onChangePage,
  rowsPerPage,
  onChangeRowPage,
  refetchProjects,
  rowsPerPageOptions,
  processingProjects,
}) => {
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loadingCreateProject, setLoadingCreateProject] = useState(false);
  const [projectDelete, setProjectDelete] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);

  const createSrtProjectEnabled = getFeatureValue(
    FEATURE_KEYS.CREATE_SRT_PROJECT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const newUiProjectsEnabled = getFeatureValue(FEATURE_KEYS.NEW_UI_PROJECTS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const autoSaveEnabled = getFeatureValue(FEATURE_KEYS.PROJECT_AUTO_SAVE, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const presentationProjectsEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_PROJECTS,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const bulkDeleteProjectsEnabled = getFeatureValue(
    FEATURE_KEYS.BULK_DELETE_PROJECTS,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const projectsOnPage = projects;
  const isDeletingManyProjects =
    bulkDeleteProjectsEnabled &&
    selectedProjects.length > 0 &&
    projectDelete &&
    Object.keys(projectDelete).length === 0;

  const handleSelectProjects = (list) => setSelectedProjects(list);

  const handleToggleSelectAll = () => {
    if (selectedProjects.length > 0) {
      setSelectedProjects([]);
      return;
    }
    const dedupedByIds = [
      ...new Map(projectsOnPage.map((prj) => [prj.id, prj])).values(),
    ];
    setSelectedProjects(dedupedByIds);
  };

  const handleDeleteSelectedProjects = () => setProjectDelete({});

  const handleConfirmDeleteSelectedProjects = async () => {
    try {
      const projectIds = selectedProjects.map((project) => project.id);
      const response = await deleteManyProjects(projectIds);
      if (response && response.status) {
        dispatch(
          actions.noti.push({
            severity: 'success',
            message: t('deleteProjectSuccess'),
          }),
        );
        if (onDeleteManySuccess) onDeleteManySuccess();
      } else {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: t('deleteProjectError'),
          }),
        );
      }
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message:
            error?.response?.data?.errorMessage || t('deleteProjectError'),
        }),
      );
    }
    setSelectedProjects([]);
  };

  const handleCloseActionDialog = () => setProjectDelete(null);
  const handleOpenDeleteDialog = (project) => {
    setProjectDelete(project);
  };

  const handleCreateProject = async (data) => {
    const res = await createProject(data);
    if (res.status) {
      // TODO: show toast
      refetchProjects(false);
    }
    return res;
  };

  const handleCreateSampleProject = async () => {
    const data = {
      title: 'Dự án mẫu',
      product: 'TTS',
      isDeleted: false,
      blocks: [
        {
          id: uuidv4(),
          text: '',
          characters: 0,
          speed: 1,
          elements: [],
        },
      ],
    };
    setLoadingCreateProject(true);
    const res = await handleCreateProject(data);
    if (res.status) {
      setLoadingCreateProject(false);
      const idProject = res?.result?.project?.id;
      trackProjectCreated({
        product: data.product,
        sourceType: MOE_PROJECT_SOURCE_TYPE.SAMPLE,
      });
      history.push(`${route.PROJECTS}/${idProject}`, {
        isSampleProject: true,
      });
    }
    return res;
  };

  const handleConfirmDeleteProject = async (project) => {
    try {
      const idProject = project.id;
      const response = await deleteProject(idProject);
      if (response && response.status) {
        dispatch(
          actions.noti.push({
            severity: 'success',
            message: t('deleteProjectSuccess'),
          }),
        );
        if (onDeleteSuccess) onDeleteSuccess(idProject);
      } else {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: t('deleteProjectError'),
          }),
        );
      }
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message:
            error?.response?.data?.errorMessage || t('deleteProjectError'),
        }),
      );
    }
  };

  const getExtractionStatus = (row) => {
    const mapEntry = processingProjects?.get(row.id);
    if (mapEntry) return mapEntry.status;
    return row.source?.extraction?.response?.status;
  };

  const columns = [
    {
      id: 'title',
      field: 'title',
      align: 'left',
      width: '100%',
      cellWidth: '100%',
      render: (row) => (
        <TitleProject
          title={row.title}
          updatedAt={row.updatedAt}
          blocks={row.blocks}
          id={row.id}
          product={row.product}
          source={row.source}
          refetchProjects={refetchProjects}
          newUiProjectsEnabled={
            newUiProjectsEnabled || presentationProjectsEnabled
          }
          autoSaveEnabled={autoSaveEnabled}
          isExtracting={getExtractionStatus(row) === EXTRACTION_STATUS.PENDING}
          bulkDeleteProjectsEnabled={bulkDeleteProjectsEnabled}
        />
      ),
    },
    {
      id: 'actions',
      field: 'actions',
      width: '100%',
      cellWidth: '100%',
      render: (row) => (
        <Actions
          project={row}
          handleOpenDeleteDialog={() => handleOpenDeleteDialog(row)}
          extractionStatus={getExtractionStatus(row)}
          errorMessage={processingProjects?.get(row.id)?.errorMessage}
        />
      ),
    },
  ];

  const handleRedirectToProjectDetails = (id) => {
    const project = projects.find((prj) => prj.id === id);
    const extractionStatus = getExtractionStatus(project);
    if (extractionStatus === EXTRACTION_STATUS.FAILURE) return;
    history.push(`${route.PROJECTS}/${id}`);
    dispatch(actions.project.setProduct(project?.product));
  };

  const getDeleteDescription = () => {
    const content = isDeletingManyProjects
      ? t('deleteManyProjectsDescription', { count: selectedProjects.length })
      : t('deleteProjectDescription', { projectName: projectDelete?.title });
    return sanitize(content, { allowedTags: ['strong'] });
  };

  useEffect(() => {
    if (!projectsOnPage) return;
    const currentProjectIds = new Set(
      projectsOnPage.map((project) => project.id),
    );
    const remainingSelectedProjects = selectedProjects.filter((project) =>
      currentProjectIds.has(project.id),
    );
    if (remainingSelectedProjects.length !== selectedProjects.length) {
      setSelectedProjects(remainingSelectedProjects);
    }
  }, [projectsOnPage]);

  return (
    <StyledProjectList>
      <Header
        search={search}
        onSearch={handleSearchInputChange}
        showInputSearch={createSrtProjectEnabled ? hasAnyProject : true}
        bulkDeleteEnabled={bulkDeleteProjectsEnabled}
        selectedProjects={selectedProjects}
        projectsOnPage={projectsOnPage}
        onToggleSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleDeleteSelectedProjects}
      />
      {!search && !hasAnyProject && !loading ? (
        <>
          <BannerCreateSampleProject
            handleCreateSampleProject={handleCreateSampleProject}
            loadingCreateProject={loadingCreateProject}
          />
          {createSrtProjectEnabled && (
            <NoData height="308px" noDataText={t('projectNotFound')} />
          )}
        </>
      ) : (
        <Table
          columns={columns}
          data={projectsOnPage}
          page={page}
          total={total}
          onChangeRowPage={onChangeRowPage}
          loading={loading}
          rowsPerPage={rowsPerPage}
          onChangePage={onChangePage}
          onRowClick={(id) => handleRedirectToProjectDetails(id)}
          footerPadding="0px 20px 0px"
          isStickyHeader
          isShowCheckbox={Boolean(bulkDeleteProjectsEnabled)}
          selected={selectedProjects}
          onSelect={handleSelectProjects}
          heightTableProjects="425px"
          isExpand
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
      {projectDelete && (
        <NotificationDialogV3
          renderIcon={() => (
            <DeleteIcon
              style={{
                color: '#FF3B30',
                fontSize: '24px',
              }}
            />
          )}
          widthContent="512px"
          name="delete-project"
          title={t('deleteProject')}
          padding="12px 24px"
          paddingHeader="8px 24px"
          description={getDeleteDescription()}
          open={!!projectDelete}
          onClose={() => {
            handleCloseActionDialog();
          }}
          actionComponent={
            <div className="">
              <Button
                className="btn-understood"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDeletingManyProjects) {
                    handleConfirmDeleteSelectedProjects();
                  } else {
                    handleConfirmDeleteProject(projectDelete || {});
                  }
                  handleCloseActionDialog();
                }}
              >
                {t('delete')}
              </Button>
              <Button
                className="btn-understood"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseActionDialog();
                }}
              >
                {t('cancel')}
              </Button>
            </div>
          }
        />
      )}
    </StyledProjectList>
  );
};

export default ProjectList;
